import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { registerSW } from "virtual:pwa-register";
import { initPostHog } from "./lib/posthog";
import App from "./App.tsx";
import "./index.css";

// Initialize PostHog analytics (no-ops if VITE_POSTHOG_KEY not set)
initPostHog();

// Initialize Sentry for error monitoring
// DSN configured via VITE_SENTRY_DSN environment variable
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,

    // Performance monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Capture 10% of all sessions
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Tracing sample rate (10% in production, 100% in development)
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session replay sample rate
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Environment tagging
    environment: import.meta.env.MODE,

    // Release tracking (set by CI/CD or Vite build)
    release: import.meta.env.VITE_SENTRY_RELEASE || "myna-ai@1.0.0",

    // Filter out common non-actionable errors
    ignoreErrors: [
      // Browser extensions
      "top.GLOBALS",
      "Can't find variable: __firefox__",
      "chrome-extension://",
      "moz-extension://",

      // Network errors (handled by app)
      "Failed to fetch",
      "NetworkError",
      "Load failed",

      // ResizeObserver (non-critical)
      "ResizeObserver loop",
    ],

    // Add user context if available
    beforeSend(event) {
      // Optionally enrich with user context from auth state
      return event;
    },
  });
}

// Register service worker for PWA support (offline + install prompt)
const updateSW = registerSW({
  onNeedRefresh() {
    // Prompt user when new version is available
    if (confirm("A new version of MYNA AI is available. Reload to update?")) {
      updateSW(true);
    }
  },
  onOfflineReady() {
    if (import.meta.env.DEV) console.log("MYNA AI is ready to work offline");
  },
});

createRoot(document.getElementById("root")!).render(<App />);
